import type { Query, QueryClient } from '@tanstack/react-query';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { getActiveQueryClient } from '../active-query-client.js';
import { refetchRealtimeQuery } from './refetchRealtimeQuery.js';
import { recordBackerRewardDiagnostic } from './scoped-entity-store.js';

/** Mark the shared entity and every viewer feed stale after a confirmed back.
 * The active SDK client is provider-owned, so all three web backing entry
 * points can call this once from their shared transaction helper. */
export async function reconcileBackerRewardAfterBacking(marketPubkey: string): Promise<void> {
  const queryClient = getActiveQueryClient();
  if (queryClient === undefined) return;
  await reconcileBackerRewardQueries(queryClient, marketPubkey, 'backing');
}

/** Reconcile every materialization after a confirmed claim. Inactive entries
 * are marked stale for their next mount; mounted feeds/detail entities and the
 * profile portfolio snapshot refetch immediately through their real queryFn. */
export async function reconcileBackerRewardAfterClaim(
  queryClient: QueryClient,
  marketPubkey: string,
): Promise<void> {
  await reconcileBackerRewardQueries(queryClient, marketPubkey, 'claim');
}

export async function reconcileBackerRewardQueries(
  queryClient: QueryClient,
  marketPubkey: string,
  reason: 'backing' | 'claim',
): Promise<void> {
  const isTarget = (query: Query): boolean => {
    if (query.meta?.specName === 'useGetBackerRewards') return true;
    // Account totals are server-aggregated with no realtime channel, so a
    // back or claim must refetch them to move the portfolio tiles.
    if (query.meta?.specName === 'useGetBackerRewardsSummary') return true;
    if (reason === 'claim' && query.meta?.specName === 'useGetProfilePortfolioSnapshot') {
      return true;
    }
    const key = query.queryKey;
    return key[0] === 'backer-rewards' && key[key.length - 1] === marketPubkey;
  };
  await queryClient.invalidateQueries({ predicate: isTarget, refetchType: 'none' });
  const targets = queryClient
    .getQueryCache()
    .findAll()
    .filter((query) => isTarget(query) && query.getObserversCount() > 0);
  const results = await Promise.allSettled(
    targets.map((query) => refetchRealtimeQuery(queryClient, query.queryKey)),
  );
  if (results.every((result) => result.status === 'fulfilled' && result.value !== 'error')) {
    return;
  }
  recordBackerRewardDiagnostic({
    kind: reason === 'claim' ? 'claim-reconciliation-failure' : 'backing-reconciliation-failure',
    entityHash: hashDiagnosticEntity(marketPubkey),
    reason: 'refetch-failed',
  });
}
